/**
 * Frontend error telemetry (Mark's directive 2026-08-17).
 *
 * The client speaks Sentry's PUBLISHED protocol via the official
 * @sentry/vue SDK (pinned 7.120.4), so the ingest backend is swappable by
 * changing ONE build-time value: today the DSN points at our own API, which
 * logs every event into `docker logs`; set VUE_APP_SENTRY_DSN to a DSN from
 * Sentry SaaS, self-hosted Sentry, or GlitchTip, rebuild, and the identical
 * client reports there instead. Zero code changes. See
 * ai_docs/ops/frontend_telemetry.md.
 *
 * Motivating incident: the pdf.js module worker was served as
 * application/octet-stream, the browser refused the module script, the
 * field-placement designer failed with "Could not load the template" —
 * while every server log showed 200s. Only the devtools console knew. From
 * now on that class of failure is REPORTED: window.onerror, unhandled
 * rejections, and Vue errorHandler throws all become envelopes POSTed to
 * the DSN's ingest endpoint.
 *
 * DSN → URL mapping (verified against @sentry/core 7.120.4,
 * getBaseApiEndpoint):
 *
 *   DSN  {protocol}://{key}@{host}/{path}/{projectId}
 *   URL  {protocol}://{host}/{path}/api/{projectId}/envelope/?sentry_key=…
 *
 * Our self-ingest DSN is `<origin>` + `/api/client-errors/1`, so the SDK
 * posts to `<origin>/api/client-errors/api/1/envelope/`, which
 * Routes/index.js registers (the inner `/api/` is the protocol's own fixed
 * segment; the project id must be numeric — the SDK validates /^\d+$/).
 */
// A NAMED import, never `import * as Sentry`: every @sentry package is
// ESM-with-sideEffects:false, and @sentry/browser v7 statically re-exports
// Replay, Feedback and BrowserTracing — a namespace object passed around at
// runtime forces webpack to keep all of them (~+310 KB min / ~+100 KB gzip,
// measured). Pulling only `init` lets webpack tree-shake everything this
// errors-only setup does not use.
import { init as sentryInit } from '@sentry/vue';

// The public key is protocol furniture: the SDK requires one (\w+) and sends
// it as ?sentry_key=…, and our ingest ignores it — an error report from a
// logged-out browser is still an error report.
const SELF_KEY = 'ladeway';
const SELF_PATH = 'api/client-errors';
const SELF_PROJECT_ID = '1';

/**
 * Resolve the DSN for this build/page.
 *
 * - An explicit VUE_APP_SENTRY_DSN wins (that is the whole swap story).
 * - The literal 'off' (or empty in development) disables telemetry with no
 *   console noise — dev default is OFF so `npm run dev` stays quiet.
 * - A production build with no DSN configured self-ingests: the DSN is
 *   derived from the page's own origin at runtime, because this app is
 *   deployed under more than one hostname and nginx proxies /api on all of
 *   them; baking one hostname in at build time would misreport for every
 *   other origin.
 */
export function resolveDsn({ dsn, nodeEnv, location }) {
  if (dsn === 'off') return '';
  if (dsn) return dsn;
  if (nodeEnv !== 'production') return '';
  if (!location || !location.host) return '';
  return `${location.protocol}//${SELF_KEY}@${location.host}/${SELF_PATH}/${SELF_PROJECT_ID}`;
}

/**
 * The `environment` an event is filed under. The public demo is the same
 * frontend image as production under another hostname (the same exact-match
 * gate header.vue and login.html use), and a stranger poking at a
 * sandbox must not be filed next to a paying tenant's errors.
 */
export function environmentFor({ nodeEnv, location }) {
  if (nodeEnv !== 'production') return 'development';
  return location && location.hostname === 'demo.ladeway.com' ? 'demo' : 'production';
}

/**
 * Scrub an outgoing event. The ingest endpoint (ours today, possibly a
 * third party after a DSN swap) must never receive cookies, auth headers,
 * localStorage, or form values:
 *
 * - cookies/headers: the SDK's HttpContext integration only attaches url,
 *   referrer and UA, but a future SDK bump or a stray integration must not
 *   widen that quietly, so everything except User-Agent/Referer is dropped
 *   here, at the last exit.
 * - localStorage / form values: never collected in the first place — no
 *   integration here reads them, and default DOM breadcrumbs record element
 *   selectors, not values. This function is the backstop, not the policy.
 */
export function scrubEvent(event) {
  if (event && event.request) {
    delete event.request.cookies;
    delete event.request.env;
    if (event.request.headers) {
      const kept = {};
      for (const name of Object.keys(event.request.headers)) {
        const lower = name.toLowerCase();
        if (lower === 'user-agent' || lower === 'referer') {
          kept[name] = event.request.headers[name];
        }
      }
      event.request.headers = kept;
    }
  }
  if (event && event.user) delete event.user;
  return event;
}

/**
 * Initialize the SDK. Called once from main.js, before the app mounts, so
 * boot-time failures (the /users/me fetch, router setup) are captured too.
 * Returns true when telemetry is on — main.js does not care, but the tests
 * do.
 */
export function initTelemetry({
  app,
  sentry = { init: sentryInit },
  env = process.env,
  location = window.location,
}) {
  const dsn = resolveDsn({
    dsn: env.VUE_APP_SENTRY_DSN,
    nodeEnv: env.NODE_ENV,
    location,
  });
  if (!dsn) return false;

  sentry.init({
    app,
    dsn,
    // The build's git sha (webpack DefinePlugin, ui/config/prod.env.js), so
    // a logged event names the exact frontend build that threw — minified
    // frame offsets are only meaningful against the bundle that made them.
    release: env.GIT_SHA || undefined,
    environment: environmentFor({ nodeEnv: env.NODE_ENV, location }),

    // Errors only. No tracing (no BrowserTracing integration, no
    // tracesSampleRate: v7 keeps tracing fully off unless asked), no session
    // "health" pings, no client-outcome reports: our ingest would drop those
    // envelope items anyway, and every one of them is bytes on the user's
    // uplink and noise in our rate-limit budget.
    autoSessionTracking: false,
    sendClientReports: false,

    // Default integrations stay ON: GlobalHandlers (window.onerror +
    // onunhandledrejection), Breadcrumbs, LinkedErrors, Dedupe, HttpContext,
    // InboundFilters, TryCatch — plus the Vue integration this package adds,
    // which wraps app.config.errorHandler so component render/lifecycle
    // throws are captured (logErrors: true below keeps them ALSO visible in
    // the devtools console, matching Vue's own default behavior).
    logErrors: true,

    beforeSend(event) {
      return scrubEvent(event);
    },
  });
  return true;
}
